/**
 * CanvasTestUtilities - Test utilities for EdgeDocs Canvas
 *
 * Extracted from EdgeDocsCanvas.V3.tsx for better modularity and testability.
 *
 * This utility provides test functions for debugging canvas operations,
 * incremental layout, and WASM integration.
 */

import type { TypesetterWorkerClient } from '../../../../workers/TypesetterWorkerClient';

export interface CanvasTestDependencies {
  typesetterClientRef: React.MutableRefObject<TypesetterWorkerClient | null>;
  currentDocumentTextRef: React.MutableRefObject<string>;
  currentSnapshotRef: React.MutableRefObject<any>;
  fontFamily: string;
  onDocumentTextChange?: (newText: string) => void;
}

/**
 * Setup test event listeners for canvas debugging
 *
 * @param deps - Canvas dependencies needed for testing
 * @returns Cleanup function to remove event listeners
 */
export function setupCanvasTestListeners(deps: CanvasTestDependencies): () => void {
  const { typesetterClientRef, currentDocumentTextRef, currentSnapshotRef, fontFamily, onDocumentTextChange } = deps;

  const testIncrementalLayout = async () => {
    console.log('🧪 ========== INCREMENTAL LAYOUT TEST START ==========');

    const oldText = currentDocumentTextRef.current || '';
    const newText = oldText + ' TEST';

    console.log('🧪 Step 1: Current state', {
      oldTextLen: oldText.length,
      newTextLen: newText.length,
      currentSnapshot: currentSnapshotRef.current?.lines?.map((l: any) =>
        `[${l.start_grapheme_index}→${l.end_grapheme_index}]`
      )
    });

    try {
      const result = await typesetterClientRef.current?.applyIncrementalTextChange(
        oldText,
        newText,
        oldText.length,
        oldText.length,
        fontFamily,
        400,
        'normal'
      );

      console.log('🧪 Step 2: Incremental result', result);

      console.log('🧪 Step 3: Snapshot after', {
        currentSnapshot: currentSnapshotRef.current?.lines?.map((l: any) =>
          `[${l.start_grapheme_index}→${l.end_grapheme_index}]`
        )
      });

      const passed = result && result.affectedParagraphIds.length > 0;

      window.dispatchEvent(new CustomEvent('edgedocs:test-result', {
        detail: {
          timestamp: new Date().toISOString(),
          passed,
          message: passed
            ? `Affected ${result.affectedParagraphIds.length} paragraphs, cacheUpdated: ${result.cacheUpdated}`
            : 'No paragraphs affected',
          data: result
        }
      }));

      console.log('🧪 ========== TEST END ==========');
    } catch (error) {
      console.error('🧪 ❌ Test failed:', error);
      window.dispatchEvent(new CustomEvent('edgedocs:test-result', {
        detail: {
          timestamp: new Date().toISOString(),
          passed: false,
          message: `Error: ${(error as Error).message}`,
          data: { error: (error as Error).stack }
        }
      }));
    }
  };

  const testApplyDelta = async () => {
    console.log('🧪 ========== APPLY DELTA TEST START ==========');

    try {
      const delta = {
        position: (currentDocumentTextRef.current || '').length,
        deleteCount: 0,
        insertText: ' DELTA_TEST'
      };

      console.log('🧪 Applying delta:', delta);

      const result = await typesetterClientRef.current?.applyEditDelta(delta);

      console.log('🧪 Delta result:', result);

      const passed = result && result.affectedParagraphIds && result.affectedParagraphIds.length > 0;

      window.dispatchEvent(new CustomEvent('edgedocs:test-result', {
        detail: {
          timestamp: new Date().toISOString(),
          passed,
          message: passed
            ? `Delta applied: ${result.affectedParagraphIds.length} paragraphs affected`
            : 'Delta failed or no paragraphs affected',
          data: result
        }
      }));

      console.log('🧪 ========== DELTA TEST END ==========');
    } catch (error) {
      console.error('🧪 ❌ Delta test failed:', error);
      window.dispatchEvent(new CustomEvent('edgedocs:test-result', {
        detail: {
          timestamp: new Date().toISOString(),
          passed: false,
          message: `Error: ${(error as Error).message}`,
          data: { error: (error as Error).stack }
        }
      }));
    }
  };

  const testLayoutAffected = async () => {
    console.log('🧪 ========== LAYOUT AFFECTED TEST START ==========');

    try {
      const paragraphIds = ['para-0'];

      console.log('🧪 Laying out paragraphs:', paragraphIds);

      const result = await typesetterClientRef.current?.layoutAffectedParagraphs(paragraphIds);

      console.log('🧪 Layout result:', result);

      const passed = result && result.layouts && result.layouts.length > 0;

      window.dispatchEvent(new CustomEvent('edgedocs:test-result', {
        detail: {
          timestamp: new Date().toISOString(),
          passed,
          message: passed
            ? `Layout complete: ${result.layouts.length} snapshots returned`
            : 'Layout failed or no snapshots returned',
          data: result
        }
      }));

      console.log('🧪 ========== LAYOUT TEST END ==========');
    } catch (error) {
      console.error('🧪 ❌ Layout test failed:', error);
      window.dispatchEvent(new CustomEvent('edgedocs:test-result', {
        detail: {
          timestamp: new Date().toISOString(),
          passed: false,
          message: `Error: ${(error as Error).message}`,
          data: { error: (error as Error).stack }
        }
      }));
    }
  };

  const testSpacebarBug = async () => {
    console.log('🧪 ========== SPACEBAR BUG TEST START ==========');
    console.log('🧪 This test simulates: paste multi-line text → press spacebar at line start');

    try {
      // Step 1: Simulate paste
      const pasteText = 'Line 1\nLine 2\nLine 3\nLine 4';
      console.log('🧪 Step 1: Simulating paste of multi-line text');

      if (onDocumentTextChange) {
        onDocumentTextChange(pasteText);
      }

      // Wait for paste to settle
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 2: Get current state
      const beforeSnapshot = currentSnapshotRef.current?.lines?.map((l: any) =>
        `[${l.start_grapheme_index}→${l.end_grapheme_index}]`
      );
      console.log('🧪 Step 2: Line boundaries after paste:', beforeSnapshot);

      // Step 3: Insert space at beginning of line 4 (after 3 newlines)
      const spacePosition = 'Line 1\nLine 2\nLine 3\n'.length;
      const oldText = currentDocumentTextRef.current || '';
      const newText = oldText.slice(0, spacePosition) + ' ' + oldText.slice(spacePosition);

      console.log('🧪 Step 3: Inserting space at position', spacePosition);

      const result = await typesetterClientRef.current?.applyIncrementalTextChange(
        oldText,
        newText,
        spacePosition,
        spacePosition,
        fontFamily,
        400,
        'normal'
      );

      // Step 4: Check if line boundaries shifted correctly
      const afterSnapshot = currentSnapshotRef.current?.lines?.map((l: any) =>
        `[${l.start_grapheme_index}→${l.end_grapheme_index}]`
      );
      console.log('🧪 Step 4: Line boundaries after space:', afterSnapshot);
      console.log('🧪 Step 5: cacheUpdated:', result?.cacheUpdated);

      const passed = result && result.affectedParagraphIds.length > 0;

      window.dispatchEvent(new CustomEvent('edgedocs:test-result', {
        detail: {
          timestamp: new Date().toISOString(),
          passed,
          message: passed
            ? `Spacebar test: cacheUpdated=${result.cacheUpdated}, affected ${result.affectedParagraphIds.length} paragraphs`
            : 'Spacebar test failed',
          data: {
            result,
            beforeSnapshot,
            afterSnapshot,
            spacePosition
          }
        }
      }));

      console.log('🧪 ========== SPACEBAR BUG TEST END ==========');
    } catch (error) {
      console.error('🧪 ❌ Spacebar bug test failed:', error);
      window.dispatchEvent(new CustomEvent('edgedocs:test-result', {
        detail: {
          timestamp: new Date().toISOString(),
          passed: false,
          message: `Error: ${(error as Error).message}`,
          data: { error: (error as Error).stack }
        }
      }));
    }
  };

  window.addEventListener('edgedocs:test-incremental-layout', testIncrementalLayout);
  window.addEventListener('edgedocs:test-apply-delta', testApplyDelta);
  window.addEventListener('edgedocs:test-layout-affected', testLayoutAffected);
  window.addEventListener('edgedocs:test-spacebar-bug', testSpacebarBug);

  return () => {
    window.removeEventListener('edgedocs:test-incremental-layout', testIncrementalLayout);
    window.removeEventListener('edgedocs:test-apply-delta', testApplyDelta);
    window.removeEventListener('edgedocs:test-layout-affected', testLayoutAffected);
    window.removeEventListener('edgedocs:test-spacebar-bug', testSpacebarBug);
  };
}
